import React, { useState } from 'react';
import { AlertCircle, RefreshCw, Clock, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import type {
  UIMessage,
  MessagePart,
  ChatStatus,
  TypedToolPart,
  MCPToolInvocationPart,
  ToolInvocationPart,
  TaskStatusPart,
  ActivityPart,
} from '../../types/chat';
import { MessageActions } from './MessageActions';
import { Reasoning } from './Reasoning';
import { MemoryContext } from './MemoryContext';
import { InlineTaskStatus } from './InlineTaskStatus';
import { UnifiedToolCard } from './UnifiedToolCard';
import { StreamdownMessage } from '../ai/StreamdownMessage';
import { DelegationGroup } from '../ai/DelegationGroup';
import { A2AToolCard } from '../tools/A2AToolCard';
import { DelegationIndicator } from '../DelegationBadge';
import { ActivityIndicator } from './ActivityIndicator';
import { ActivityGroup } from './ActivityGroup';
import { TokenUsageSummaryCard } from './TokenUsageSummaryCard';
import { normalizeToolPart } from '../../utils/normalizeToolPart';

/** Deterministic gradient from an agent name — stable across page reloads. */
function nameToGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash * 31) + name.charCodeAt(i)) >>> 0;
  }
  const gradients = [
    'from-violet-500 to-purple-600',
    'from-emerald-500 to-teal-600',
    'from-sky-500 to-blue-600',
    'from-amber-500 to-orange-500',
    'from-rose-500 to-pink-600',
    'from-indigo-500 to-violet-600',
    'from-cyan-500 to-sky-600',
    'from-fuchsia-500 to-purple-600',
  ];
  return gradients[hash % gradients.length];
}

/** Two-character initials from an agent name. */
function toInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function formatTimestamp(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

interface MessageProps {
  message: UIMessage;
  isStreaming?: boolean;
  status?: ChatStatus;
  /** Shown under “Failed to send” when status is error */
  sendErrorDetail?: string | null;
  onRetry?: () => void;
  onRegenerate?: () => void;
  isLastAssistantMessage?: boolean;
  userId?: string;
  toolMetadata?: Map<string, {
    startedAt?: number;
    completedAt?: number;
    durationMs?: number;
    category?: string;
    serverName?: string;
    parentToolCallId?: string;
  }>;
}

function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex items-center gap-1.5">
        <span
          className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 animate-bounce shadow-sm"
          style={{ animationDelay: '0ms', animationDuration: '0.6s' }}
        />
        <span
          className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 animate-bounce shadow-sm"
          style={{ animationDelay: '150ms', animationDuration: '0.6s' }}
        />
        <span
          className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 animate-bounce shadow-sm"
          style={{ animationDelay: '300ms', animationDuration: '0.6s' }}
        />
      </div>
      <span className="text-sm text-gray-400 italic">Thinking...</span>
    </div>
  );
}

function isTypedToolPart(part: MessagePart): part is TypedToolPart {
  return (
    part.type.startsWith('tool-') &&
    part.type !== 'tool-invocation' &&
    'state' in part &&
    'toolCallId' in part
  );
}

function isDelegationTool(part: MessagePart): part is TypedToolPart {
  return isTypedToolPart(part) && part.toolName.startsWith('delegate_to_');
}

/** Returns true for any non-delegation tool part that has reached a completed state. */
function isCompletedToolPart(part: MessagePart): boolean {
  if (isTypedToolPart(part)) {
    // Exclude delegation tools — those have their own grouping
    if (part.toolName.startsWith('delegate_to_')) return false;
    return part.state === 'output-available';
  }
  if (part.type === 'tool-invocation') return (part as ToolInvocationPart).state === 'completed';
  if (part.type === 'mcp-tool-invocation') return (part as MCPToolInvocationPart).state === 'completed';
  return false;
}

function isTransientActivity(part: MessagePart): part is ActivityPart {
  return (
    part.type === 'data-activity' &&
    (part as ActivityPart).transient &&
    (part as ActivityPart).data.activityType !== 'agent-complete'
  );
}

function isCompletionActivity(part: MessagePart): part is ActivityPart {
  return (
    part.type === 'data-activity' &&
    (part as ActivityPart).data.activityType === 'agent-complete'
  );
}

type RenderItem =
  | { kind: 'single'; part: MessagePart; index: number }
  | { kind: 'delegation-group'; parts: TypedToolPart[]; startIndex: number }
  | { kind: 'activity-group'; parts: ActivityPart[]; startIndex: number }
  | { kind: 'tool-group'; parts: MessagePart[]; startIndex: number };

/**
 * Pre-process message parts to detect consecutive runs of related parts
 * and collapse them into grouped items for more compact rendering.
 */
function groupParts(parts: MessagePart[]): RenderItem[] {
  const items: RenderItem[] = [];
  let i = 0;

  while (i < parts.length) {
    // Try delegation tool group (2+ consecutive delegate_to_* parts)
    if (isDelegationTool(parts[i])) {
      const start = i;
      const group: TypedToolPart[] = [];
      while (i < parts.length && isDelegationTool(parts[i])) {
        group.push(parts[i] as TypedToolPart);
        i++;
      }
      if (group.length >= 2) {
        items.push({ kind: 'delegation-group', parts: group, startIndex: start });
      } else {
        items.push({ kind: 'single', part: group[0], index: start });
      }
      continue;
    }

    // Try transient activity group (2+ consecutive transient non-complete activities)
    if (isTransientActivity(parts[i])) {
      const start = i;
      const group: ActivityPart[] = [];
      while (i < parts.length && isTransientActivity(parts[i])) {
        group.push(parts[i] as ActivityPart);
        i++;
      }
      if (group.length >= 2) {
        items.push({ kind: 'activity-group', parts: group, startIndex: start });
      } else {
        items.push({ kind: 'single', part: group[0], index: start });
      }
      continue;
    }

    // Try completion activity group (2+ consecutive agent-complete activities)
    if (isCompletionActivity(parts[i])) {
      const start = i;
      const group: ActivityPart[] = [];
      while (i < parts.length && isCompletionActivity(parts[i])) {
        group.push(parts[i] as ActivityPart);
        i++;
      }
      if (group.length >= 2) {
        items.push({ kind: 'activity-group', parts: group, startIndex: start });
      } else {
        items.push({ kind: 'single', part: group[0], index: start });
      }
      continue;
    }

    // Try completed tool group (3+ consecutive completed non-delegation tool calls)
    if (isCompletedToolPart(parts[i])) {
      const start = i;
      const group: MessagePart[] = [];
      while (i < parts.length && isCompletedToolPart(parts[i])) {
        group.push(parts[i]);
        i++;
      }
      if (group.length >= 3) {
        items.push({ kind: 'tool-group', parts: group, startIndex: start });
      } else {
        group.forEach((p, j) => items.push({ kind: 'single', part: p, index: start + j }));
      }
      continue;
    }

    // Default: single part
    items.push({ kind: 'single', part: parts[i], index: i });
    i++;
  }

  return items;
}

function formatGroupDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function ToolGroup({
  parts,
  startIndex,
  toolMetadata,
  userId,
}: {
  parts: MessagePart[];
  startIndex: number;
  toolMetadata?: MessageProps['toolMetadata'];
  userId?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  const totalMs = parts.reduce((sum, part) => {
    const id = 'toolCallId' in part ? (part as { toolCallId: string }).toolCallId : undefined;
    return sum + (toolMetadata?.get(id ?? '')?.durationMs ?? 0);
  }, 0);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-hidden">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        )}
        <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
        <span className="text-sm text-gray-600 dark:text-gray-300 font-medium flex-1">
          {parts.length} tools completed
        </span>
        {totalMs > 0 && (
          <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatGroupDuration(totalMs)}
          </span>
        )}
      </button>
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-2 space-y-1">
          {parts.map((part, j) => renderMessagePart(part, startIndex + j, false, userId, toolMetadata))}
        </div>
      )}
    </div>
  );
}

function renderMessagePart(
  part: MessagePart,
  index: number,
  isStreaming: boolean = false,
  userId?: string,
  toolMetadata?: MessageProps['toolMetadata'],
): React.ReactNode {
  if (isTypedToolPart(part)) {
    // Use A2AToolCard for A2A delegation tools
    if (part.toolName === 'delegate_to_remote') {
      return (
        <A2AToolCard
          key={index}
          toolCallId={part.toolCallId}
          state={part.state}
          input={part.input}
          output={part.output}
          errorText={part.errorText}
        />
      );
    }
    const unified = normalizeToolPart(part as TypedToolPart, toolMetadata?.get(part.toolCallId));
    return <UnifiedToolCard key={index} part={unified} />;
  }

  switch (part.type) {
    case 'data-memoryContext':
      // Memory context is rendered inline in the message header, skip here
      return null;
    case 'data-planStatus':
      // Plan status is displayed in the sidebar panel, skip inline rendering
      return null;
    case 'data-audio': {
      const format = part.data.format || 'wav';
      const src = part.data.url
        ? part.data.url
        : part.data.base64
          ? `data:audio/${format};base64,${part.data.base64}`
          : null;
      const label = part.data.isFinal === false ? 'Audio (streaming)' : 'Audio';

      return (
        <div
          key={index}
          className="my-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{label}</div>
          {src ? (
            <audio controls src={src} className="w-full" />
          ) : (
            <div className="text-xs text-gray-500 dark:text-gray-400">Audio chunk received.</div>
          )}
        </div>
      );
    }
    case 'data-structuredOutput': {
      const title = part.data.schemaName
        ? `Structured Output (${part.data.schemaName})`
        : 'Structured Output';
      const metadataParts = [
        part.data.source ? `source=${part.data.source}` : null,
        part.data.toolCallId ? `toolCallId=${part.data.toolCallId}` : null,
        part.data.version ? `version=${part.data.version}` : null,
      ].filter(Boolean);
      const metadataLine =
        metadataParts.length > 0 ? metadataParts.join(' | ') : null;

      return (
        <div
          key={index}
          className="my-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{title}</div>
          {metadataLine && (
            <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{metadataLine}</div>
          )}
          <pre className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 rounded p-2 overflow-x-auto border border-gray-100 dark:border-gray-700">
            {JSON.stringify(part.data.payload, null, 2)}
          </pre>
        </div>
      );
    }
    case 'task-status':
      return (
        <InlineTaskStatus key={index} part={part} isStreaming={isStreaming} />
      );
    case 'data-activity': {
      const activityPart = part as ActivityPart;
      if (activityPart.data.activityType === 'agent-complete') {
        return <TokenUsageSummaryCard key={index} part={activityPart} />;
      }
      return <ActivityIndicator key={index} part={activityPart} />;
    }
    case 'text':
      if (!part.text.trim()) return null;
      return (
        <StreamdownMessage
          key={index}
          content={part.text}
          isStreaming={isStreaming}
        />
      );
    case 'reasoning':
      return (
        <Reasoning
          key={index}
          text={part.text}
          isStreaming={isStreaming}
          defaultExpanded={isStreaming}
        />
      );
    case 'source':
      return (
        <a
          key={index}
          href={part.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-600 hover:underline text-sm inline-flex items-center gap-1"
        >
          {part.title || part.url}
        </a>
      );
    case 'mcp-tool-invocation': {
      const unified = normalizeToolPart(part as MCPToolInvocationPart);
      return <UnifiedToolCard key={index} part={unified} />;
    }
    case 'tool-invocation': {
      const p = part as ToolInvocationPart;
      const unified = normalizeToolPart(p, toolMetadata?.get(p.toolCallId));
      return <UnifiedToolCard key={index} part={unified} />;
    }
  }
}

export function Message({
  message,
  isStreaming,
  status,
  sendErrorDetail,
  onRetry,
  onRegenerate,
  isLastAssistantMessage = false,
  userId,
  toolMetadata,
}: MessageProps) {
  const { selectedAgent } = useAgent();
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  const agentName = message.metadata?.agent_name ?? selectedAgent?.name ?? 'AI';
  const agentGradient = nameToGradient(agentName);
  const agentInitials = toInitials(agentName);
  const isError = status === 'error';
  const showActions =
    isAssistant && !isStreaming && isLastAssistantMessage && status === 'ready';

  const textContent = message.parts
    .filter((p): p is MessagePart & { type: 'text' } => p.type === 'text')
    .map((p) => p.text)
    .join('\n');

  // Memory-only messages are handled inline in the header, no special case needed

  const timestamp = message.createdAt
    ? formatTimestamp(message.createdAt)
    : null;

  return (
    <div
      className={`group/message w-full flex items-start gap-2 md:gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300 ${
        isUser ? 'justify-end' : 'justify-start'
      }`}
      data-testid={`message-${message.role}`}
      data-message-id={message.id}
    >
      {/* Assistant avatar — colour derived from agent name, shows initials */}
      {isAssistant && (
        <div className={`flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br ${agentGradient} flex items-center justify-center shadow-md ring-2 ring-white dark:ring-gray-900`}>
          <span className="text-xs font-bold text-white tracking-wide select-none">{agentInitials}</span>
        </div>
      )}

      {/* Message content */}
      <div
        className={`flex flex-col gap-1.5 ${
          isUser ? 'items-end' : 'items-start'
        } max-w-[85%] md:max-w-[75%]`}
      >
        {/* Role label, timestamp, delegation badge, and inline memory indicator */}
        <div
          className={`flex items-center gap-2 text-xs ${
            isUser ? 'flex-row-reverse' : 'flex-row'
          }`}
        >
          <span
            className={`font-medium ${isUser ? 'text-sky-600 dark:text-sky-400' : 'text-emerald-600 dark:text-emerald-400'}`}
          >
            {isUser ? 'You' : message.metadata?.agent_name || 'Assistant'}
          </span>
          {/* Delegation badge - shows which specialist handled the request */}
          {isAssistant && message.metadata?.delegation && (
            <DelegationIndicator delegation={message.metadata.delegation} />
          )}
          {timestamp && (
            <span className="text-gray-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timestamp}
            </span>
          )}
          {/* Provider response ID for tracing (AgentScope v1.0.18+) */}
          {isAssistant && message.metadata?.invocationId && (
            <span
              className="text-[10px] text-gray-400 dark:text-gray-600 font-mono cursor-pointer hover:text-gray-600 dark:hover:text-gray-400"
              title="Click to copy provider response ID"
              onClick={() => navigator.clipboard.writeText(message.metadata!.invocationId!)}
            >
              {message.metadata.invocationId.length > 20
                ? `${message.metadata.invocationId.slice(0, 8)}...${message.metadata.invocationId.slice(-6)}`
                : message.metadata.invocationId}
            </span>
          )}
          {/* Inline memory context - rendered in header for minimal space */}
          {isAssistant &&
            message.parts
              .filter((p) => p.type === 'data-memoryContext')
              .map((part, idx) =>
                part.type === 'data-memoryContext' ? (
                  <MemoryContext
                    key={`memory-${idx}`}
                    memories={part.data.memories}
                    userId={userId}
                  />
                ) : null,
              )}
        </div>

        {isUser ? (
          /* User message bubble */
          <div className="break-words w-fit rounded-2xl rounded-tr-md px-4 py-2.5 text-white bg-gradient-to-r from-sky-600 to-sky-500 shadow-md hover:shadow-lg transition-shadow">
            {message.parts.map((part, index) =>
              part.type === 'text' ? (
                <span key={index} className="whitespace-pre-wrap">
                  {part.text}
                </span>
              ) : null,
            )}
          </div>
        ) : (
          /* Assistant message - clean, no bubble */
          <div className="text-gray-800 dark:text-gray-100 w-full">
            {message.parts.length === 0 && isStreaming ? (
              <ThinkingIndicator />
            ) : (
              <div className="space-y-2" data-testid="message-content">
                {groupParts(message.parts).map((item) => {
                  if (item.kind === 'delegation-group') {
                    return (
                      <DelegationGroup
                        key={`dg-${item.startIndex}`}
                        parts={item.parts}
                      />
                    );
                  }
                  if (item.kind === 'activity-group') {
                    return (
                      <ActivityGroup
                        key={`ag-${item.startIndex}`}
                        parts={item.parts}
                      />
                    );
                  }
                  if (item.kind === 'tool-group') {
                    return (
                      <ToolGroup
                        key={`tg-${item.startIndex}`}
                        parts={item.parts}
                        startIndex={item.startIndex}
                        toolMetadata={toolMetadata}
                        userId={userId}
                      />
                    );
                  }
                  return renderMessagePart(
                    item.part,
                    item.index,
                    isStreaming,
                    userId,
                    toolMetadata,
                  );
                })}
              </div>
            )}

            {isStreaming && message.parts.length > 0 && (
              <span className="inline-block w-0.5 h-5 bg-emerald-500 animate-pulse ml-0.5 align-middle rounded-full" />
            )}
          </div>
        )}

        {/* Error state */}
        {isError && onRetry && (
          <div className="mt-2 flex flex-col gap-1.5 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 shadow-sm max-w-full">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-600 dark:text-red-400 font-medium">Failed to send</span>
              <button
                onClick={onRetry}
                className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium ml-2 px-2 py-1 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
            {sendErrorDetail ? (
              <p
                className="text-xs text-red-600/90 dark:text-red-400/90 font-mono break-all pl-6"
                title={sendErrorDetail}
              >
                {sendErrorDetail}
              </p>
            ) : null}
          </div>
        )}

        {/* Actions - appear on hover */}
        {showActions && textContent && (
          <div className="opacity-100 sm:opacity-0 sm:group-hover/message:opacity-100 sm:group-focus-within/message:opacity-100 transition-all duration-200 -mt-1">
            <MessageActions
              text={textContent}
              onRegenerate={onRegenerate}
              showRegenerate={!!onRegenerate}
            />
          </div>
        )}
      </div>

      {/* User avatar */}
      {isUser && (
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 via-sky-500 to-cyan-600 flex items-center justify-center shadow-md ring-2 ring-white dark:ring-gray-900">
          <span className="text-xs font-bold text-white tracking-wide select-none">Me</span>
        </div>
      )}
    </div>
  );
}
